import StrategyProfile from "./Profile";
import DividerLine from "components/common/divider/line";
import StrategyPerformance from "./Performance";
import StrategyReturns from "./Returns";
import { STRATEGY_TYPE } from "utils/constant/STATUS";
import { TYPO } from "styles/Typo";
import StrategyTabs, { moveStickyTab } from "./Tabs";
import FixedButton from "components/common/buttons/FixedButtons";
import useIsMobile from "states/deviceState";
import { useRouter } from "next/router";
import Button from "components/common/buttons/buttons";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, createContext, useContext } from "react";
import { useTabTracking } from "lib/analytics/react/hooks";

export type StrategyContextValue = {
  iso: string | undefined;
};

export const StrategyContext = createContext<StrategyContextValue | undefined>(
  undefined
);

export const useStrategyContext = (): StrategyContextValue => {
  const context = useContext(StrategyContext);
  return context ?? { iso: undefined };
};

export const useStrategyIso = (): string | undefined => {
  const { iso } = useStrategyContext();
  return iso;
};

function StrategyTemplete({
  sCode,
  data,
  assets,
  realtimeData,
}: {
  sCode: STRATEGY_TYPE;
  data: any;
  assets: any;
  realtimeData: any;
}) {
  const router = useRouter();
  const [init, setInit] = useState(false);
  const { type } = router.query;
  const { t } = useTranslation("strategy");
  const isMobile = useIsMobile();
  const [faqAutoExpanded, setFaqAutoExpanded] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [fixedButtonVisible, setFixedButtonVisible] = useState(false);
  const { trackClick } = useTabTracking("strategy_tab_click", [
    "performance",
    "holdings",
  ]);
  useEffect(() => {
    if (profileRef.current) {
      if (type === "performance") {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              setFixedButtonVisible(true);
            } else {
              setFixedButtonVisible(false);
            }
          });
        });
        observer.observe(profileRef.current);
      }
    }
  }, [profileRef.current]);

  useEffect(() => {
    if (type === "holdings") {
      setFaqAutoExpanded(false);
    }
  }, [type]);

  if (!data)
    return (
      <div
        className={`${TYPO.TYPO6_SB} w-full flex items-center justify-center h-screen`}
      ></div>
    );
  const {
    changePeriod,
    strategyLogReturns,
    kospiLogReturns,
    nasdaqLogReturns,
    totalBaseDT,
    strategyRisk,
    iso,
  } = data;

  return (
    <StrategyContext.Provider value={{ iso }}>
      <div className="">
        <StrategyProfile
          sCode={sCode}
          logMock={strategyLogReturns}
          risk={strategyRisk}
          changeTime={changePeriod}
          assetCount={assets?.list?.length}
          profileRef={profileRef}
          setFaqAutoExpanded={setFaqAutoExpanded}
          realtimeData={realtimeData}
        />
        <DividerLine size="xl" />
        <StrategyTabs isMobile={isMobile} />
        {type === "performance" && (
          <div className="strategy-tab-section">
            <StrategyPerformance
              logMock={strategyLogReturns}
              kospiLogMock={kospiLogReturns}
              nasdaqLogMock={nasdaqLogReturns}
              risk={strategyRisk}
              sCode={sCode}
              faqAutoExpanded={faqAutoExpanded}
              setFaqAutoExpanded={setFaqAutoExpanded}
              realtimeData={realtimeData}
            />
          </div>
        )}
        {(type === "holdings" || !type) && (
          <div className="strategy-tab-section">
            <StrategyReturns
              logMock={strategyLogReturns}
              kospiLogReturns={kospiLogReturns}
              nasdaqLogReturns={nasdaqLogReturns}
              standardBaseDtList={totalBaseDT}
              sCode={sCode}
              init={init}
              setInit={setInit}
              realtimeData={realtimeData}
            />
          </div>
        )}

        {isMobile ? (
          <div
            className={router.query.type === "holdings" ? "hidden" : ""}
            style={
              {
                // display: router.query.type === "holdings" ? "none" : "",
              }
            }
          >
            <FixedButton
              isDesktop={!isMobile}
              rounded={true}
              type={"primary"}
              size={"xl"}
              text={t("전략 종목 보기")}
              zIndex={50}
              onclick={async () => {
                trackClick(
                  "holdings",
                  "shortcut",
                  "strategy_navbtn_view_stocks"
                );
                router.replace({ query: { type: "holdings" } }, undefined, {
                  shallow: true,
                });
                setTimeout(() => {
                  moveStickyTab(isMobile);
                }, 10);
              }}
              dataGtmId="stViewStockList"
              customStyle={{
                transform: !fixedButtonVisible
                  ? "translateY(100%)"
                  : "translateY(0)",
                transition: "transform 0.5s ease-in-out",
              }}
            />
          </div>
        ) : (
          <div
            className={` 
            ${router.query.type === "holdings" ? "hidden" : ""}
          z-[40] bg-white w-[800px] px-5 pt-4 pb-[30px] fixed bottom-0 left-[50%] translate-x-[-50%] !font-semibold `}
            onClick={async () => {
              trackClick("holdings", "shortcut", "strategy_navbtn_view_stocks");
              router.replace({ query: { type: "holdings" } }, undefined, {
                shallow: true,
              });
              setTimeout(() => {
                moveStickyTab(isMobile);
              }, 10);
            }}
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.85) 10%, rgba(255, 255, 255, 0.95) 40.14%, #FFF 100%)",
              transform: !fixedButtonVisible
                ? "translate(-50%,100%)"
                : "translate(-50%,0)",
              transition: "transform 0.5s ease-in-out",
            }}
            data-gtm-id="stViewStockList"
          >
            <Button
              type="primary"
              size="lg"
              text={t("전략 종목 보기")}
              width="w-full"
            />
          </div>
        )}
      </div>
    </StrategyContext.Provider>
  );
}

export default StrategyTemplete;
